import React, { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  LocationOn as LocationIcon,
  Thermostat as ThermostatIcon,
  WaterDrop as HumidityIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import { useGetHumidorsQuery, useDeleteHumidorMutation } from '@store/api/humidorApi'
import { useAppSelector } from '@store/hooks'
import HumidorForm from '../components/humidors/HumidorForm'
import HumidorEditForm from '../components/humidors/HumidorEditForm'
import HumidorDetails from '../components/humidors/HumidorDetails'
import { Humidor } from '@store/api/humidorApi'
import PageHeader from '@components/layout/PageHeader'
import { trackFeature } from '@utils/trackFeature'

const Humidors: React.FC = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [humidorToDelete, setHumidorToDelete] = useState<Humidor | null>(null)
  const [createFormOpen, setCreateFormOpen] = useState(false)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedHumidor, setSelectedHumidor] = useState<Humidor | null>(null)

  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { data: humidors = [], isLoading, error } = useGetHumidorsQuery(undefined, {
    skip: !isAuthenticated
  })
  const [deleteHumidor] = useDeleteHumidorMutation()

  const handleDeleteClick = (humidor: Humidor) => {
    setHumidorToDelete(humidor)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (humidorToDelete) {
      try {
        await deleteHumidor(humidorToDelete.id).unwrap()
        setDeleteDialogOpen(false)
        setHumidorToDelete(null)
      } catch (error) {
        console.error('Failed to delete humidor:', error)
      }
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setHumidorToDelete(null)
  }

  const handleEditClick = (humidor: Humidor) => {
    setSelectedHumidor(humidor)
    setEditFormOpen(true)
  }

  const handleEditSuccess = () => {
    setEditFormOpen(false)
    setSelectedHumidor(null)
  }

  const handleDetailsClick = (humidor: Humidor) => {
    setSelectedHumidor(humidor)
    setDetailsDialogOpen(true)
  }

  const handleDetailsEdit = (humidor: Humidor) => {
    setDetailsDialogOpen(false)
    handleEditClick(humidor)
  }

  const handleDetailsDelete = (humidor: Humidor) => {
    setDetailsDialogOpen(false)
    handleDeleteClick(humidor)
  }

  const getCapacityColor = (usage: number) => {
    if (usage >= 90) return 'error'
    if (usage >= 75) return 'warning'
    return 'success'
  }

  const getTemperatureColor = (temp?: number, target?: number) => {
    if (!temp || !target) return 'default'
    const diff = Math.abs(temp - target)
    if (diff <= 2) return 'success'
    if (diff <= 5) return 'warning'
    return 'error'
  }

  const getHumidityColor = (humidity?: number, target?: number) => {
    if (!humidity || !target) return 'default'
    const diff = Math.abs(humidity - target)
    if (diff <= 5) return 'success'
    if (diff <= 10) return 'warning'
    return 'error'
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Humidors" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load humidors. Please try again.
      </Alert>
    )
  }

  return (
    <>
      <PageHeader
        title="Humidors"
        primaryAction={{
          label: 'Add humidor',
          icon: <AddIcon />,
          onClick: () => setCreateFormOpen(true),
        }}
      />

      {humidors.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <StorageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Humidors Yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Create your first humidor to start organizing your cigar collection.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                trackFeature('empty_state_cta', { action: 'add_humidor' })
                setCreateFormOpen(true)
              }}
            >
              Create Your First Humidor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {humidors.map((humidor) => (
            <Grid item xs={12} sm={6} md={4} key={humidor.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" noWrap>
                      {humidor.name}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(humidor)}
                        color="error"
                        aria-label={`Delete humidor ${humidor.name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {humidor.location && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {humidor.location}
                      </Typography>
                    </Box>
                  )}

                  {/* Capacity */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Capacity
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {humidor._count?.cigars ?? 0} / {humidor.capacity}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={humidor.capacityUsage}
                      color={getCapacityColor(humidor.capacityUsage)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  {/* Temperature & Humidity */}
                  <Grid container spacing={1}>
                    {humidor.currentTemperature && (
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <ThermostatIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {humidor.currentTemperature}°F
                          </Typography>
                          {humidor.temperatureTarget && (
                            <Chip
                              size="small"
                              label={`Target: ${humidor.temperatureTarget}°F`}
                              color={getTemperatureColor(humidor.currentTemperature, humidor.temperatureTarget)}
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                    {humidor.currentHumidity && (
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <HumidityIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {humidor.currentHumidity}%
                          </Typography>
                          {humidor.humidityTarget && (
                            <Chip
                              size="small"
                              label={`Target: ${humidor.humidityTarget}%`}
                              color={getHumidityColor(humidor.currentHumidity, humidor.humidityTarget)}
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {humidor.notes && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {humidor.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<InfoIcon />}
                    onClick={() => handleDetailsClick(humidor)}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(humidor)}
                  >
                    Edit
                  </Button>
                  {(humidor._count?.cigars ?? 0) === 0 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(humidor)}
                    >
                      Delete
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Humidor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &ldquo;{humidorToDelete?.name}&rdquo;? This action cannot be undone.
          </Typography>
          {humidorToDelete && (humidorToDelete._count?.cigars ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This humidor contains {humidorToDelete._count?.cigars ?? 0} cigars. Please move or delete them first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={(humidorToDelete?._count?.cigars ?? 0) > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Humidor Form Dialog */}
      <HumidorForm
        open={createFormOpen}
        onClose={() => setCreateFormOpen(false)}
        onSuccess={() => {
          // Refresh data handled automatically by RTK Query cache invalidation
        }}
      />

      {/* Edit Humidor Form Dialog */}
      {selectedHumidor && (
        <HumidorEditForm
          open={editFormOpen}
          onClose={() => {
            setEditFormOpen(false)
            setSelectedHumidor(null)
          }}
          onSuccess={handleEditSuccess}
          humidor={selectedHumidor}
        />
      )}

      {/* Humidor Details Dialog */}
      {selectedHumidor && (
        <HumidorDetails
          open={detailsDialogOpen}
          onClose={() => {
            setDetailsDialogOpen(false)
            setSelectedHumidor(null)
          }}
          humidor={selectedHumidor}
          onEdit={handleDetailsEdit}
          onDelete={handleDetailsDelete}
        />
      )}
    </>
  )
}

export default Humidors
